import { useCallback, useEffect, useRef, useState } from "react";

export interface UseInactivityDetectorOptions {
  timeoutMs: number;
  warningMs: number;
  enabled: boolean;
  onFreeze: () => void;
}

export interface UseInactivityDetectorResult {
  isWarning: boolean;
  remainingSeconds: number;
  resetTimer: () => void;
}

const ACTIVITY_EVENTS = [
  "mousemove", "mousedown", "mouseup",
  "keydown", "keyup",
  "wheel",
  "touchstart", "touchmove",
] as const;

/** Debounce delay to avoid excessive resetTimer calls on frequent events (mousemove). */
const ACTIVITY_DEBOUNCE_MS = 200;

export function useInactivityDetector({
  timeoutMs,
  warningMs,
  enabled,
  onFreeze,
}: UseInactivityDetectorOptions): UseInactivityDetectorResult {
  const [isWarning, setIsWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.ceil(warningMs / 1000),
  );

  const idleTimerRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const freezeTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const debounceRef       = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningStartRef   = useRef<number>(0);
  const onFreezeRef       = useRef(onFreeze);
  onFreezeRef.current     = onFreeze;

  const clearAll = useCallback(() => {
    if (debounceRef.current)    { clearTimeout(debounceRef.current);     debounceRef.current = null; }
    if (idleTimerRef.current)   { clearTimeout(idleTimerRef.current);    idleTimerRef.current = null; }
    if (freezeTimerRef.current) { clearTimeout(freezeTimerRef.current);  freezeTimerRef.current = null; }
    if (countdownRef.current)   { clearInterval(countdownRef.current);   countdownRef.current = null; }
  }, []);

  const resetTimer = useCallback(() => {
    clearAll();
    setIsWarning(false);
    setRemainingSeconds(Math.ceil(warningMs / 1000));

    idleTimerRef.current = setTimeout(() => {
      setIsWarning(true);
      warningStartRef.current = Date.now();
      setRemainingSeconds(Math.ceil(warningMs / 1000));

      countdownRef.current = setInterval(() => {
        const elapsed = Date.now() - warningStartRef.current;
        const rem = Math.ceil((warningMs - elapsed) / 1000);
        setRemainingSeconds(Math.max(0, rem));
      }, 500);

      freezeTimerRef.current = setTimeout(() => {
        onFreezeRef.current();
      }, warningMs);
    }, timeoutMs - warningMs);
  }, [clearAll, timeoutMs, warningMs]);

  useEffect(() => {
    if (!enabled) {
      clearAll();
      setIsWarning(false);
      setRemainingSeconds(Math.ceil(warningMs / 1000));
      return;
    }

    resetTimer();

    // Debounce activity resets to avoid excessive clearTimeout/setTimeout on mousemove
    const handleActivity = () => {
      if (debounceRef.current) return;
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        resetTimer();
      }, ACTIVITY_DEBOUNCE_MS);
    };
    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, handleActivity, { passive: true }),
    );

    const handleVisibility = () => {
      if (document.hidden) {
        clearAll();
        setIsWarning(false);
      } else {
        resetTimer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearAll();
      ACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, handleActivity),
      );
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [enabled, resetTimer, clearAll, warningMs]);

  return { isWarning, remainingSeconds, resetTimer };
}
